/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {isDeviceAlert} from '@ion/core/src/services/AlertUtils';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {TrashCanIcon} from '@ion/dls/src/icons/TrashCanIcon';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import Render from '../render/Render';

export interface NotificationCardCTAType {
  handleLessClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  handleMoreClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  handleClearClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  moreInfoLoading?: boolean;
  isHideViewEventCta?: boolean;
  viewEventCta?: JSX.Element | null;
  showMoreInfo?: boolean;
  notificationHoverCta?: string;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    notificationCardActionsRow : {
      display        : 'flex',
      marginTop      : '51px',
      alignItems     : 'center',
      justifyContent : 'space-between'
    },
    // per Figma's "Frame 5556" (this row) / "Frame 5554" (this group), the
    // View Event/Read More group is a plain flex child with no left offset -
    // the previous `left:'26px', position:'relative'` had no basis in the
    // spec and was nudging the whole group further right/left than Figma's
    // flush-left, space-between layout intends ...
    notificationCardActionCTA : {
      gap     : '8px',
      display : 'flex'
    },
    deleteContainer : {
      gap            : '8px',
      cursor         : 'pointer',
      display        : 'flex',
      padding        : '4px 8px 4px 8px',
      alignItems     : 'center',
      justifyContent : 'space-between'
    },
    clearText : {
      color         : dlsColor('grey1'),
      fontSize      : '12px',
      fontFamily    : 'Inter',
      fontWeight    : 500,
      lineHeight    : '16px',
      letterSpacing : '0px'
    }
  };
});

/**
 * Component for rendering notification card CTA buttons
 *
 * ```typescript
 * 
 * import {NotificationHeaderCTA} from '@ion/dls/src/notifications'
 *
 * ```
 *
 */
export const NotificationCardCTA = ({
  viewEventCta,
  handleLessClick,
  handleMoreClick,
  handleClearClick,
  notificationHoverCta,
  isHideViewEventCta = false,
  moreInfoLoading = false,
  showMoreInfo = false
}: NotificationCardCTAType) => {
  const classes = useStyles();

  return (
    <div className={classes.notificationCardActionsRow}>
      <div className={classes.notificationCardActionCTA}>
        {!isHideViewEventCta && viewEventCta}
        <TertiaryButton onClick={showMoreInfo ? handleLessClick : handleMoreClick}
          disabled={moreInfoLoading}>
          <Render>
            <Render.If condition={moreInfoLoading}>
              <IonSpinner width='20px' height='20px' />
            </Render.If>
            <Render.Else>
              {translate(showMoreInfo ? 'notifications:panel.readLess' : 'notifications:panel.readMore')}
            </Render.Else>
          </Render>
        </TertiaryButton>
      </div>
      <div className={notificationHoverCta}>
        <Hidden hide='mobile'>
          <div className={classes.deleteContainer} onClick={handleClearClick}>
            <span>
              {/* icon defaults to grey2 (useIconColor's dlsIconColor()
                  fallback) - Figma specs grey1 for this icon, matching the
                  clearText color below it ... */}
              <TrashCanIcon color={dlsColor('grey1')} />
            </span>
            <span className={classes.clearText}>
              {translate('notifications:panel.clear')}
            </span>
          </div>
        </Hidden>
      </div>
    </div>
  );
};

export default React.memo(NotificationCardCTA);
